import { type AbortReason } from '@/lib/constants/post-message-types';

export interface ResponseTrackingContextValue {
  pendingResponsesRef: React.RefObject<Map<string, number>>;
  abortReasonsRef: React.RefObject<Map<string, AbortReason>>;
  streamingStartedRef: React.RefObject<Set<string>>;
  /** Thread ids whose current pending response is a retry (manual or auto). */
  retriedResponsesRef: React.RefObject<Set<string>>;
}

export interface InterruptedEventPayload {
  threadId: string;
  requestDurationMs: number;
  streamingDurationMs?: number;
  abortReason: AbortReason;
  isDocumentHidden: boolean;
  isNetworkOffline: boolean;
  hadStreamingStarted: boolean;
  isRetry: boolean;
  isPagePersisted?: boolean;
}

interface BuildInterruptedEventPayloadParams {
  threadId: string;
  startTime: number;
  hadStreamingStarted: boolean;
  abortReason: AbortReason;
  isRetry: boolean;
  isPagePersisted?: boolean;
}

export function buildInterruptedEventPayload({
  threadId,
  startTime,
  hadStreamingStarted,
  abortReason,
  isRetry,
  isPagePersisted,
}: BuildInterruptedEventPayloadParams): InterruptedEventPayload {
  const durationMs = Date.now() - startTime;

  return {
    threadId,
    requestDurationMs: durationMs,
    ...(hadStreamingStarted && { streamingDurationMs: durationMs }),
    abortReason,
    isDocumentHidden: document.visibilityState === 'hidden',
    isNetworkOffline: !navigator.onLine,
    hadStreamingStarted,
    isRetry,
    ...(isPagePersisted !== undefined && { isPagePersisted }),
  };
}

export function cleanupPendingResponse(
  context: ResponseTrackingContextValue | null,
  threadId: string,
): void {
  context?.pendingResponsesRef.current?.delete(threadId);
  context?.streamingStartedRef.current?.delete(threadId);
  context?.retriedResponsesRef.current?.delete(threadId);
}

export function getHadStreamingStarted(
  context: ResponseTrackingContextValue | null,
  threadId: string,
): boolean {
  return context?.streamingStartedRef.current?.has(threadId) ?? false;
}

export function getIsRetry(
  context: ResponseTrackingContextValue | null,
  threadId: string,
): boolean {
  return context?.retriedResponsesRef.current?.has(threadId) ?? false;
}
